import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  Chip,
  Box,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  BugReport as BugIcon,
  Lightbulb as IdeaIcon,
  Send as SendIcon,
  ViewKanban as BoardIcon,
} from '@mui/icons-material';
import { Link as MuiLink } from '@mui/material';
import { createRequest, getRequestType, type RequestDocument } from '../api/requests';
import PendingAttachments, { type PendingAttachmentsHandle } from './PendingAttachments';
import { newEvidenceSubmission, submitWithEvidence } from '../api/bugEvidence';
import { useAuth } from '../AuthContext';
import { isAccessibilityAuditSession } from '../accessibilityAudit';

export type FeedbackKind = 'bug' | 'idea';

// Optional tag chips shown in the modal. Joined to a comma-separated string and stored on the
// request (and rendered into the ADO work item description).
export const FEEDBACK_TAGS = [
  'ui/ux',
  'data',
  'performance',
  'security',
  'integration',
  'automation',
] as const;

export type FeedbackTag = (typeof FEEDBACK_TAGS)[number];

export const TAG_COLORS: Record<
  FeedbackTag,
  'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default'
> = {
  'ui/ux': 'info',
  data: 'secondary',
  performance: 'warning',
  security: 'error',
  integration: 'primary',
  automation: 'success',
};

// Each feedback kind maps to a governed ADO-backed request, also visible in My Requests.
const KIND_TO_REQUEST_TYPE: Record<FeedbackKind, string> = { bug: 'bug', idea: 'idea' };

interface Props {
  open: boolean;
  /** When set, the type is locked (Header quick-submit). When omitted, the user picks Bug/Idea. */
  presetKind?: FeedbackKind;
  onClose: () => void;
  /** Optional callback after a successful submit, invoked before navigation. */
  onSubmitted?: (created: RequestDocument) => void;
  /** Prefill the title field (e.g. Ops-Center "File Bug" seeds the failed step). */
  prefillTitle?: string;
  /** Prefill the description field. */
  prefillDescription?: string;
  /**
   * When set, the created bug's ADO work item is linked back to this MsApprove request via
   * <c>/api/msapprove/operations/link-bug</c> and the modal does NOT navigate to the new request —
   * the caller stays on the Ops Center. Only meaningful for bug submissions.
   */
  linkToRequestId?: string;
  /** Invoked after a successful link-back (Ops-Center flow) with the linked ADO work item id. */
  onLinked?: (workItemId: number) => void;
}

const KIND_LABEL: Record<FeedbackKind, string> = { bug: 'Bug', idea: 'Idea' };

const FeedbackSubmitModal: React.FC<Props> = ({
  open, presetKind, onClose, onSubmitted, prefillTitle, prefillDescription, linkToRequestId, onLinked,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const auditReadOnly = isAccessibilityAuditSession(user);
  const [kind, setKind] = React.useState<FeedbackKind>(presetKind ?? (auditReadOnly ? 'bug' : 'idea'));
  const canSubmitFeedback = !auditReadOnly || kind === 'bug';
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [tags, setTags] = React.useState<FeedbackTag[]>([]);
  const [customTags, setCustomTags] = React.useState('');
  const [evidenceEnabled, setEvidenceEnabled] = React.useState(false);
  const evidence = React.useRef(newEvidenceSubmission());
  const pendingAttachments = React.useRef<PendingAttachmentsHandle>(null);
  const busyRef = React.useRef(false);
  const [sourceUrl, setSourceUrl] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Reset the form and capture context each time the modal opens.
  React.useEffect(() => {
    if (!open) return;
    setKind(presetKind ?? (auditReadOnly ? 'bug' : 'idea'));
    setTitle(prefillTitle ?? '');
    setDescription(prefillDescription ?? '');
    setTags([]);
    setCustomTags('');
    setEvidenceEnabled(false);
    evidence.current = newEvidenceSubmission();
    setSourceUrl(window.location.href);
    setError(null);
    let active = true;
    void getRequestType('bug').then(template => {
      if (active) setEvidenceEnabled(template.attachments?.enabled === true && template.attachments.provider === 'ado');
    }).catch(err => {
      if (active) setError(`Bug evidence settings could not be loaded: ${err instanceof Error ? err.message : 'Request failed'}`);
    });
    return () => { active = false; };
  }, [open, presetKind, prefillTitle, prefillDescription, auditReadOnly]);

  const toggleTag = (tag: FeedbackTag) => {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const handleSubmit = async () => {
    if (busyRef.current) return;
    if (!canSubmitFeedback) {
      setError('Accessibility auditors can report bugs, but cannot submit ideas.');
      return;
    }
    if (!title.trim() || !description.trim()) {
      setError('Title and description are both required.');
      return;
    }
    busyRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const linkWorkItem = async (workItemId: number) => {
        const res = await fetch('/api/msapprove/operations/link-bug', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ requestId: linkToRequestId, workItemId }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(
            `Bug #${workItemId} was created, but linking it to the request failed: ${
              body.error || body.detail || `HTTP ${res.status}`
            }`,
          );
        }
      };

      const created = await submitWithEvidence(evidence.current, () => createRequest(KIND_TO_REQUEST_TYPE[kind], {
        title: title.trim(),
        description: description.trim(),
        tags: [...tags, ...(kind === 'bug' && evidenceEnabled ? customTags.split(',').map(t => t.trim()).filter(Boolean) : [])].join(','),
        sourceUrl: sourceUrl || '',
      }), kind === 'bug' && evidenceEnabled ? pendingAttachments.current?.getFiles() ?? [] : []);

      // Ops-Center flow: link the freshly-created Bug Board work item back to the failed MsApprove
      // request so the operations dashboard tracks its open/resolved state. Stay on the Ops Center
      // (no navigation) so the admin keeps their place in the failed-steps list.
      if (linkToRequestId && !auditReadOnly) {
        const workItemId = Number(created?.data?.adoWorkItemId);
        if (!Number.isInteger(workItemId) || workItemId <= 0) {
          throw new Error(
            'Bug was created but its ADO work item id was unavailable, so it could not be linked. ' +
              'Use "Link Existing Item" to attach it manually.',
          );
        }
        await linkWorkItem(workItemId);
        onLinked?.(workItemId);
        onSubmitted?.(created);
        onClose();
        return;
      }

      onSubmitted?.(created);
      onClose();
      if (created?.id) navigate(`/requests/${created.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit feedback');
    } finally {
      busyRef.current = false;
      setSubmitting(false);
    }
  };

  const titleText = presetKind
    ? presetKind === 'bug'
      ? 'Report a bug'
      : 'Share an idea'
    : 'Submit feedback';

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          {kind === 'bug' ? <BugIcon color="error" /> : <IdeaIcon color="warning" />}
          <span>{titleText}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {evidence.current.created && <Alert severity="info">
            Your bug is saved. Retry completes the remaining attachment or link step without creating another bug.{' '}
            <MuiLink href={`/requests/${evidence.current.created.id}`}>View saved request</MuiLink>
          </Alert>}
          {auditReadOnly && (
            <Alert severity="info">
              Report a Bug is the only submission allowed in audit mode.
              {linkToRequestId && ' The bug will not change or link back to this operational request.'}
            </Alert>
          )}

          <MuiLink
            component="button"
            type="button"
            underline="hover"
            disabled={submitting}
            onClick={() => { if (submitting) return; onClose(); navigate(`/boards/${kind}`); }}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5, alignSelf: 'flex-start', fontSize: '0.85rem',
              ...(submitting && { pointerEvents: 'none', opacity: 0.6 }),
            }}
          >
            <BoardIcon fontSize="small" /> Browse the {KIND_LABEL[kind]} board
          </MuiLink>

          {!presetKind && (
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              color="primary"
              value={kind}
              disabled={submitting || !!evidence.current.created}
              onChange={(_, v) => v && setKind(v)}
            >
              <ToggleButton value="bug">
                <BugIcon fontSize="small" sx={{ mr: 1 }} /> Bug
              </ToggleButton>
              <ToggleButton value="idea">
                <IdeaIcon fontSize="small" sx={{ mr: 1 }} /> Idea
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          <TextField
            label="Title"
            required
            fullWidth
            size="small"
            value={title}
            disabled={submitting || !!evidence.current.created}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 255 }}
            helperText={`${title.length}/255 — submitting as ${KIND_LABEL[kind]}`}
          />
          <TextField
            label="Description"
            required
            fullWidth
            multiline
            minRows={5}
            value={description}
            disabled={submitting || !!evidence.current.created}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              kind === 'bug'
                ? 'What went wrong? Include steps to reproduce, expected vs actual behavior, and any links.'
                : "What's the idea or improvement? Include context and the problem it solves."
            }
          />

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Tags (optional)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {FEEDBACK_TAGS.map((tag) => {
                const selected = tags.includes(tag);
                return (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    color={selected ? TAG_COLORS[tag] : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => toggleTag(tag)}
                    disabled={submitting || !!evidence.current.created}
                  />
                );
              })}
            </Box>
            {kind === 'bug' && evidenceEnabled && <>
              <TextField label="Custom tags (optional)" value={customTags}
                disabled={submitting || !!evidence.current.created}
                onChange={e => setCustomTags(e.target.value)}
                helperText="Separate with commas. Up to 99 distinct tags including selected suggestions, 50 characters each. One additional tag is reserved for the AskIAM board within Azure DevOps' 100-tag save limit." />
              <PendingAttachments ref={pendingAttachments} disabled={submitting || !!evidence.current.created} />
              <Typography variant="caption">Attachments go to the Azure DevOps bug work item. Do not include credentials or sensitive personal data.</Typography>
            </>}
          </Box>

          {sourceUrl && (
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              Submitted from: {sourceUrl}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={() => void handleSubmit()}
          variant="contained"
          disabled={submitting || !canSubmitFeedback || !title.trim() || !description.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {evidence.current.created ? 'Retry remaining steps' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackSubmitModal;
